/**
 * 承認（案件を跨ぐ一覧 1 本・承認の書込 1 本）と用語の備考の書込 1 本の TanStack Query ラッパー。
 *
 * **旧い承認のフック（`spec-pj-queries.ts`）とはファイルを分ける。** 相手にする表が違い
 * （設計どおりの `spec_graph_*` と、T1a で消した旧い形の表）、行が指す相手も違う（ノードの id と
 * 変更行の id）。
 * 1 つのファイルに新旧を並べると、切替の段で消す側と残す側が同じ import 元になり、消し残りが
 * 型で見えなくなる。
 *
 * **案件番号は number である**（決定 2）。今の承認が受ける文字列の案件番号とは型が違うので、
 * 呼び出し側が取り違えたまま通ることはない。
 *
 * 承認を積んだら一覧を取り直す。一覧は案件を持たないので、言語を問わず全部を取り直す —
 * 承認状態は言語に依らないため、いま見ている言語だけ取り直すと、言語を切り替えた瞬間に
 * 古い状態が現れる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の一覧と承認 / 却下の書込と用語の備考の書込を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specPj.listTermChangesAcrossPjs
 * @graph-connects rpcClient [calls] specPj.approveTermChanges
 * @graph-connects rpcClient [calls] specPj.updateTermNote
 */
import type {
  ApproveTermChangesInput,
  ApproveTermChangesOutput,
  ListTermChangesAcrossPjsOutput,
  UpdateTermNoteInput,
  UpdateTermNoteOutput,
} from '@cortex/domain-spec-console';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';

/**
 * 未確定の案件に積まれた承認の対象を、案件を跨いで読む。
 *
 * `enabled` は本人が担当者だと分かってから true にする。担当者でない人が呼ぶと API は
 * `FORBIDDEN` を返すので、照合（`useApprovalAccess`）を待ってから問い合わせる。
 *
 * @param enabled 問い合わせてよいか（本人が担当者か）
 * @returns 承認の一覧
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の一覧を取得する。
 * @graph-connects rpcClient [calls] specPj.listTermChangesAcrossPjs
 */
export function useTermChangesAcrossPjs(
  enabled: boolean,
): UseQueryResult<ListTermChangesAcrossPjsOutput> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specPj.listTermChangesAcrossPjs(locale),
    queryFn: () =>
      rpcClient.specPj.listTermChangesAcrossPjs(locale === undefined ? {} : { locale }),
    enabled,
  });
}

/**
 * 案件の変更のノードに承認 / 却下を積む。
 *
 * 成功したら一覧を全言語ぶん取り直す。指す相手がノードの id なので、取り直す単位も案件では
 * なく一覧そのものである（一覧は案件を跨ぐ）。
 *
 * @returns 承認の mutation
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認 / 却下を積み、承認の一覧を取り直す。
 * @graph-connects rpcClient [calls] specPj.approveTermChanges
 */
export function useApproveTermChanges(): UseMutationResult<
  ApproveTermChangesOutput,
  Error,
  ApproveTermChangesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApproveTermChangesInput) => rpcClient.specPj.approveTermChanges(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.specPj.termChangesAcrossPjsAll(),
      });
    },
  });
}

/**
 * 用語の備考を書く（画面からだけ書ける欄）。書けたら読取をすべて取り直す — 備考は用語の詳細と
 * 案件の差分・承認の一覧のどれにも出るので、キーを 1 つずつ選ぶと取り直し漏れが残る。
 *
 * @returns 用語の備考の書込
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の備考を案件へ書き、書けたら画面の読取を取り直す。
 * @graph-connects rpcClient [calls] specPj.updateTermNote
 */
export function useUpdateTermNote(): UseMutationResult<
  UpdateTermNoteOutput,
  Error,
  UpdateTermNoteInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTermNoteInput) => rpcClient.specPj.updateTermNote(input),
    onSuccess: async (result) => {
      if (result.outcome === 'applied') await queryClient.invalidateQueries();
    },
  });
}
